/**
 * 領域ラスタの数学（エンジン共通層・純関数）。
 *
 * ズームしたとき、ページ全体を高解像度で作り直すと canvas/テクスチャの上限
 * （面積 5.2MP・一辺 4096px）に頭打ちする。可視領域だけを切って要求すれば、
 * 同じ上限のまま実効解像度を上げられる — これが設計書 L361 の「可視領域の
 * 部分再描画＋上限キャップ」であり、iOS で拡大が破綻しない理由でもある。
 *
 * 座標系:
 *   - 本ローカル座標 = 本（見開き 1 枚分の矩形）の左上を原点とする CSS px
 *   - ページ内正規化座標 = ページの左上を原点とする 0〜1（PageRegion）
 */
import { FULL_PAGE, type PageRegion } from '../core/source';

export interface Box {
  width: number;
  height: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * 本ローカル座標での可視範囲。
 * 本は中央基準で translate(pan + shift) → scale(zoom) される（両エンジン共通の変換）。
 */
export function visibleBox(
  container: Box,
  book: Box,
  zoom: number,
  pan: { x: number; y: number },
  shift: number,
): Rect {
  return {
    x: book.width / 2 - (container.width / 2 + pan.x + shift) / zoom,
    y: book.height / 2 - (container.height / 2 + pan.y) / zoom,
    w: container.width / zoom,
    h: container.height / zoom,
  };
}

/** ページ矩形（本ローカル）と可視範囲の交差を、ページ内の正規化領域へ写す。交差なしは null。 */
export function pageRegion(page: Rect, visible: Rect): PageRegion | null {
  const left = Math.max(page.x, visible.x);
  const top = Math.max(page.y, visible.y);
  const right = Math.min(page.x + page.w, visible.x + visible.w);
  const bottom = Math.min(page.y + page.h, visible.y + visible.h);
  if (right <= left || bottom <= top) return null;
  return {
    x: (left - page.x) / page.w,
    y: (top - page.y) / page.h,
    w: (right - left) / page.w,
    h: (bottom - top) / page.h,
  };
}

/**
 * 余白を足す（小さなパンのたびに作り直さないため）。ページ内へクランプする。
 *
 * 余白は「可視領域自体に対する割合」で足す。ページに対する固定割合にすると、
 * 拡大するほど可視領域は小さくなるのに余白は変わらず、取得するタイルが何倍にも
 * 膨らむ（実測: ページ比 8% の余白で、面積が 2.3 倍になっていた）。
 */
export function inflateRegion(region: PageRegion, ratio: number): PageRegion {
  const marginX = region.w * ratio;
  const marginY = region.h * ratio;
  const x = Math.max(0, region.x - marginX);
  const y = Math.max(0, region.y - marginY);
  return {
    x,
    y,
    w: Math.min(1 - x, region.w + marginX * 2),
    h: Math.min(1 - y, region.h + marginY * 2),
  };
}

/**
 * 格子へ外向きに丸める（同じ格子に収まる限り既存ラスタを再利用できる）。
 * step は 0〜1 の分割幅（例 1/16）。
 */
export function quantizeRegion(region: PageRegion, step: number): PageRegion {
  const x = Math.max(0, Math.floor(region.x / step) * step);
  const y = Math.max(0, Math.floor(region.y / step) * step);
  const right = Math.min(1, Math.ceil((region.x + region.w) / step) * step);
  const bottom = Math.min(1, Math.ceil((region.y + region.h) / step) * step);
  return { x, y, w: right - x, h: bottom - y };
}

// 領域の同値判定と「全面か」の判定は領域契約そのものなので core/source.ts が持つ。
// engine 側は使い勝手のためここから再輸出する（値の定義点は 1 か所）。
export { isFullPage, sameRegion } from '../core/source';

export interface RasterPlan {
  /** 要求する物理ピクセル幅（領域の幅）。 */
  targetWidth: number;
  region: PageRegion;
}

/**
 * 上限（1 枚のラスタとして作れる寸法）を守りつつ、要求を 1 枚のラスタへ落とす。
 *
 * 領域が小さいほど、同じ上限でより高い実効解像度を出せる — これが M4 の要。
 * 例: A4・DPR2・幅 900px・zoom 4 → 全面換算 7200px（従来は上限 3200px で頭打ち）。
 *     可視領域が 1/4 四方なら、領域ラスタは 1800px 幅（面積 4.6MP < 5.2MP）に収まり、
 *     実効解像度は 7200px 相当になる。
 */
export function planRaster(input: {
  /** ページ全面換算の希望幅（box.width * dpr * zoom）。 */
  effectiveWidth: number;
  /** ページの縦横比（高さ / 幅）。 */
  aspect: number;
  region: PageRegion;
  /** 原本が供給できる全面換算の上限（拡大上限 = 生成解像度）。 */
  maxEffectiveWidth: number;
  /** 1 枚のラスタとして作れる最大幅（この縦横比での canvas 上限）。 */
  maxRasterWidth: number;
  maxArea: number;
  maxSide: number;
}): RasterPlan {
  const { region, aspect, maxArea, maxSide } = input;
  const effective = Math.min(input.effectiveWidth, input.maxEffectiveWidth);

  // 領域の縦横比（この矩形をラスタにしたときの 高さ / 幅）
  const regionAspect = (aspect * region.h) / region.w;

  const byArea = Math.sqrt(maxArea / regionAspect);
  const width = Math.min(
    effective * region.w,
    input.maxRasterWidth,
    byArea,
    maxSide,
    maxSide / regionAspect,
  );
  return { targetWidth: Math.max(1, Math.floor(width)), region };
}
